import type { AppDefinition, AppTheme } from '@/types/app';
import { baseTheme } from './templates/theme';

export type VibeId = 'calm' | 'playful' | 'cozy' | 'pro' | 'fresh';

export interface VibePreset {
  id: VibeId;
  label: string;
  description: string;
  theme: AppTheme;
}

// Build a full AppTheme from baseTheme, overriding colors + radius for the vibe.
function makeTheme(colors: Partial<AppTheme['colors']>, radius: Partial<AppTheme['borderRadius']>): AppTheme {
  return {
    ...baseTheme,
    colors: { ...baseTheme.colors, ...colors },
    borderRadius: { ...baseTheme.borderRadius, ...radius },
  };
}

export const VIBES: VibePreset[] = [
  { id: 'calm', label: 'Calm & Minimal', description: 'Soft neutrals, airy spacing',
    theme: makeTheme({ primary: '#64748B', secondary: '#475569', background: '#F8FAFC', surface: '#FFFFFF', text: '#0F172A', textSecondary: '#64748B', border: '#E2E8F0' }, { sm: 8, md: 14, lg: 20 }) },
  { id: 'playful', label: 'Bold & Playful', description: 'Bright, rounded, friendly',
    theme: makeTheme({ primary: '#7C3AED', secondary: '#6D28D9', background: '#FFFFFF', surface: '#FAF5FF', text: '#1F2937', textSecondary: '#6B7280', border: '#EDE9FE' }, { sm: 10, md: 18, lg: 26 }) },
  { id: 'cozy', label: 'Warm & Cozy', description: 'Warm cream and terracotta',
    // primary darkened to clear AA for white text on the primary button (#C2410C ≈ 4.6:1)
    theme: makeTheme({ primary: '#C2410C', secondary: '#9A3412', background: '#FFF8F1', surface: '#FFFFFF', text: '#1F2937', textSecondary: '#6B6055', border: '#F3E8DD' }, { sm: 6, md: 12, lg: 18 }) },
  { id: 'pro', label: 'Sleek & Professional', description: 'Crisp, tight, navy accents',
    theme: makeTheme({ primary: '#1E3A8A', secondary: '#1E40AF', background: '#FFFFFF', surface: '#F8FAFC', text: '#0F172A', textSecondary: '#475569', border: '#E2E8F0' }, { sm: 4, md: 8, lg: 12 }) },
  { id: 'fresh', label: 'Fresh & Nature', description: 'Green and breezy',
    // primary darkened to clear AA for white text on the primary button (#047857 ≈ 5.1:1)
    theme: makeTheme({ primary: '#047857', secondary: '#065F46', background: '#F0FDF4', surface: '#FFFFFF', text: '#14532D', textSecondary: '#4B7C5A', border: '#DCFCE7' }, { sm: 6, md: 14, lg: 20 }) },
];

export function vibeById(id: string | null | undefined): VibePreset | undefined {
  return VIBES.find((v) => v.id === id);
}

// Deterministically set the generated app's theme from the chosen vibe. Returns
// the original definition unchanged when no vibe is selected.
export function applyVibeTheme(def: AppDefinition, vibe: string | null | undefined): AppDefinition {
  const preset = vibeById(vibe);
  if (!preset) return def;
  return { ...def, theme: preset.theme };
}
